import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Observable, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { DocumentFlag } from '../../../core/models/DocumentFlag';
import { FreightDocumentFlagService } from '../../../core/services/freight-document-flag.service';
import { AlertService } from '../../../core/services/alert.service';

@Component({
  selector: 'app-document-flags',
  templateUrl: './document-flags.component.html',
  styleUrls: ['./document-flags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFlagsComponent implements OnInit {
  @Input({ required: true }) freightDocumentId: string;
  @Input() flags: DocumentFlag[] = [];
  @Input() editable = true;
  @Input() maxVisible = 3;
  @Output() flagsChange = new EventEmitter<DocumentFlag[]>();

  protected readonly plusIcon = faPlus;
  protected availableFlags$: Observable<DocumentFlag[]>;
  protected hasActiveFlags$: Observable<boolean> = of(false);
  protected readonly pendingFlagIds = new Set<number>();

  constructor(
    private readonly flagService: FreightDocumentFlagService,
    private readonly alertService: AlertService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.editable) {
      this.hasActiveFlags$ = this.flagService.hasActiveFlags();
    }
  }

  protected get visibleFlags(): DocumentFlag[] {
    return (this.flags ?? []).slice(0, this.maxVisible);
  }

  protected get hiddenFlags(): DocumentFlag[] {
    return (this.flags ?? []).slice(this.maxVisible);
  }

  protected onPickerOpenChange(open: boolean): void {
    if (open) {
      this.availableFlags$ = this.flagService.getAll().pipe(
        map(flags => flags.filter(flag => !flag.archived || this.hasFlag(flag)))
      );
    }
  }

  protected hasFlag(flag: DocumentFlag): boolean {
    return (this.flags ?? []).some(applied => applied.flagId === flag.flagId);
  }

  protected toggle(flag: DocumentFlag): void {
    if (this.pendingFlagIds.has(flag.flagId)) {
      return;
    }
    this.pendingFlagIds.add(flag.flagId);
    const previous = this.flags ?? [];
    const adding = !this.hasFlag(flag);
    const updated = adding
      ? [...previous, flag].sort((a, b) => a.name.localeCompare(b.name))
      : previous.filter(applied => applied.flagId !== flag.flagId);
    this.setFlags(updated);

    const action = adding
      ? this.flagService.addToFreightDocument(this.freightDocumentId, flag.flagId)
      : this.flagService.removeFromFreightDocument(this.freightDocumentId, flag.flagId);
    action.pipe(finalize(() => {
      this.pendingFlagIds.delete(flag.flagId);
      this.changeDetector.markForCheck();
    })).subscribe({
      error: () => {
        const current = this.flags ?? [];
        this.setFlags(adding
          ? current.filter(applied => applied.flagId !== flag.flagId)
          : [...current, flag].sort((a, b) => a.name.localeCompare(b.name)));
        this.alertService.error('alert.saveFailure');
      }
    });
  }

  private setFlags(flags: DocumentFlag[]): void {
    this.flags = flags;
    this.flagsChange.emit(flags);
    this.changeDetector.markForCheck();
  }
}
